import './PurchaseLoader.css';

/**
 * PurchaseLoader
 * SVG loading overlay for Purchase / PurchaseOrder / PurchaseReturn sections.
 * Design: animated delivery truck with spinning wheels, bobbing body,
 *         scrolling road dashes, exhaust puffs, and orbiting dots.
 *
 * Props:
 *  open    {boolean}
 *  message {string}
 *  variant {string}  'default' | 'success' | 'error' | 'warning'
 */
const PurchaseLoader = ({ open, message = 'Loading...', variant = 'default' }) => {
  if (!open) return null;

  const cx = 55, cy = 55, orbitR = 50;
  const pt = (deg) => {
    const rad = (deg - 90) * (Math.PI / 180);
    return { x: cx + orbitR * Math.cos(rad), y: cy + orbitR * Math.sin(rad) };
  };

  return (
    <div className="pu-backdrop" role="status" aria-live="polite" aria-label={message}>
      <div className={`pu-box ${variant}`}>
        <svg className="pu-svg" viewBox="0 0 110 110">

          {/* ── Orbiting dots ── */}
          <g className="pu-orbit-wrap">
            {[0, 120, 240].map((deg, i) => {
              const p = pt(deg);
              return (
                <circle key={i} cx={p.x} cy={p.y} r="4"
                  className="pu-orbit-dot"
                  fill="#1976d2" opacity="0.7" />
              );
            })}
          </g>

          {/* ── Truck group (bobs) ── */}
          <g className="pu-truck">

            {/* Cargo box */}
            <rect x="18" y="42" width="46" height="28"
              rx="3" ry="3"
              className="pu-truck-fill"
              fill="#e3f2fd" stroke="#1565c0" strokeWidth="2" />

            {/* Cab */}
            <rect x="64" y="50" width="22" height="20"
              rx="3" ry="3"
              className="pu-truck-fill"
              fill="#e3f2fd" stroke="#1565c0" strokeWidth="2" />

            {/* Cab roof slope */}
            <polygon points="64,50 78,42 86,50"
              className="pu-truck-color"
              fill="#1565c0" opacity="0.85" />

            {/* Windshield */}
            <rect x="68" y="52" width="14" height="10"
              rx="2"
              fill="#bbdefb" opacity="0.9" />

            {/* Cargo door line */}
            <line x1="18" y1="56" x2="64" y2="56"
              stroke="#1565c0" strokeWidth="1.2" opacity="0.5" />
            <line x1="41" y1="42" x2="41" y2="70"
              stroke="#1565c0" strokeWidth="1.2" opacity="0.5" />

            {/* Bumper */}
            <rect x="84" y="64" width="6" height="6"
              rx="1.5"
              className="pu-truck-color"
              fill="#1565c0" opacity="0.8" />

            {/* Rear wheel */}
            <g className="pu-wheel pu-wheel-rear"
              style={{ transformOrigin: '32px 72px' }}>
              <circle cx="32" cy="72" r="8"
                className="pu-wheel-fill"
                fill="#0d47a1" />
              <circle cx="32" cy="72" r="4"
                fill="#e3f2fd" opacity="0.8" />
              <line x1="32" y1="64" x2="32" y2="80" stroke="#e3f2fd" strokeWidth="1.2" opacity="0.6" />
              <line x1="24" y1="72" x2="40" y2="72" stroke="#e3f2fd" strokeWidth="1.2" opacity="0.6" />
            </g>

            {/* Front wheel */}
            <g className="pu-wheel"
              style={{ transformOrigin: '74px 72px' }}>
              <circle cx="74" cy="72" r="8"
                className="pu-wheel-fill"
                fill="#0d47a1" />
              <circle cx="74" cy="72" r="4"
                fill="#e3f2fd" opacity="0.8" />
              <line x1="74" y1="64" x2="74" y2="80" stroke="#e3f2fd" strokeWidth="1.2" opacity="0.6" />
              <line x1="66" y1="72" x2="82" y2="72" stroke="#e3f2fd" strokeWidth="1.2" opacity="0.6" />
            </g>

            {/* Exhaust pipe */}
            <rect x="14" y="52" width="4" height="8"
              rx="1"
              className="pu-truck-color"
              fill="#1565c0" opacity="0.7" />

            {/* Exhaust puffs */}
            <circle cx="12" cy="50" r="3"
              className="pu-puff-1"
              fill="#90caf9" opacity="0" />
            <circle cx="10" cy="46" r="4"
              className="pu-puff-2"
              fill="#90caf9" opacity="0" />
            <circle cx="8" cy="42" r="5"
              className="pu-puff-3"
              fill="#90caf9" opacity="0" />
          </g>

          {/* ── Scrolling road dashes ── */}
          <g className="pu-road-marks">
            {[10, 30, 50, 70, 90, 110].map((x, i) => (
              <rect key={i} x={x} y="82" width="14" height="3"
                rx="1.5"
                fill="#1565c0" opacity="0.25" />
            ))}
          </g>

        </svg>

        <span className="pu-label">{message}</span>
      </div>
    </div>
  );
};

export default PurchaseLoader;
